import { Component, inject, output, computed, signal, effect, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { profileStore, settingStore } from '@core/store';
import { PopoverModule, Popover } from 'primeng/popover';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { SecurityService } from '@core/services';
import { MenuBuilderService, UserRole } from '@shared/services/menu-builder.service';
import { RouterModule } from '@angular/router';
import {ADMIN_ROUTE_CONSTANTS} from '@shared/constants/routes';
import { Tenant } from '@core/types';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

interface Language {
  code: string;
  name: string;
  flag: string;
}

@Component({
  selector: 'app-header',
  standalone: true,
  templateUrl: './header.component.html',
  styleUrl: './header.component.css',
  imports: [
    CommonModule,
    PopoverModule,
    SelectModule,
    FormsModule,
    RouterModule,
    TranslateModule
  ],
})
export class HeaderComponent {
  sidebarToggle = output<void>();

  profile = inject(profileStore);
  setting = inject(settingStore);
  private menuBuilderService = inject(MenuBuilderService);

  @ViewChild('op') popover!: Popover;

  dropdownOpen = false;
  darkMode = false;

  // Language switcher properties
  languages: Language[] = [
    { code: 'es', name: 'Español', flag: '🇩🇴' },
    { code: 'en', name: 'English', flag: '🇺🇸' }
  ];

  currentLanguage = signal<Language>(this.languages[0]);




  userInitials = computed(() => {
    const firstName = this.profile.data.firstName();
    const lastName = this.profile.data.lastName();

    if (!firstName && !lastName) return '';

    const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
    const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';

    return firstInitial + lastInitial;
  });

  tenants = computed(() => this.profile.data.tenants());
  activeTenantSignal = signal<Tenant | null>(null);

  notificationCount = 3;
  securityService = inject(SecurityService);

  constructor() {
    // Sincronizar activeTenantSignal con el store
    effect(() => {
      const activeTenant = this.profile.activeTenant();
      this.activeTenantSignal.set(activeTenant);
    });

    // Sincronizar currentLanguage con el settingStore
    effect(() => {
      const lang = this.setting.data.language();
      const language = this.languages.find(l => l.code === lang) || this.languages[0];
      this.currentLanguage.set(language);
    });
  }

  onTenantChange(tenant: Tenant | null): void {
    if (!tenant) return;
    
    // Actualizar el tenant activo en el store
    // El backend validará automáticamente el tenant en cada request mediante el header X-Tenant-Id
    this.securityService.switchTenant(tenant.id);
    
    // Los condominios se refrescarán automáticamente cuando cambie el tenant
    // El activeTenantSignal se actualizará automáticamente por el effect
  }

  toggleSidebar() {
    // Solo emitir el evento, el layout principal maneja toda la lógica
    this.sidebarToggle.emit();
  }

  toggleUserMenu(event: Event) {
    event.stopPropagation();
    if (this.popover) {
      // Usar el elemento que disparó el evento como target
      const target = event.currentTarget as HTMLElement;
      this.popover.toggle(event, target);
    }
  }

  logout() {
    this.securityService.logout();
  }

  onLanguageChange(language: Language): void {
    if (language && language.code) {
      this.setting.setLanguage(language.code);
    }
  }

  protected readonly ADMIN_ROUTE_CONSTANTS = ADMIN_ROUTE_CONSTANTS;
}


